import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { membershipContent } from '../../mocks/membership-content';

export default function MembershipsPage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-white font-['Montserrat']">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold uppercase mb-6 text-black leading-tight tracking-wide">
            {membershipContent.hero.title}
          </h1>
          <p className="text-lg text-reset-sage leading-relaxed max-w-2xl mx-auto">
            {membershipContent.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 px-6 bg-reset-blue-light">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-base text-black leading-loose">
            {membershipContent.philosophy}
          </p>
        </div>
      </section>

      {/* Pilates Memberships */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs uppercase tracking-widest text-reset-sage mb-4 font-semibold">
              {membershipContent.pilates.subtitle}
            </p>
            <h2 className="text-4xl md:text-5xl font-bold uppercase mb-6 text-black tracking-wide">
              {membershipContent.pilates.title}
            </h2>
            <p className="text-base text-reset-sage leading-relaxed max-w-2xl mx-auto">
              {membershipContent.pilates.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {membershipContent.pilates.tiers.map((tier, index) => (
              <div
                key={index}
                className="bg-white border border-reset-border rounded-lg p-8 hover:border-reset-sage transition-all duration-300"
              >
                <div className="mb-6">
                  <h3 className="text-2xl font-bold uppercase mb-2 text-black tracking-wide">
                    {tier.name}
                  </h3>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-4xl font-bold text-reset-sage">
                      £{tier.price}
                    </span>
                    <span className="text-sm text-black uppercase">/ month</span>
                  </div>
                  <p className="text-sm text-black leading-relaxed mb-6">
                    {tier.description}
                  </p>
                </div>

                {/* Power of 3 Benefits */}
                <div className="mb-6 p-4 bg-reset-sage-light rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-reset-sage mb-3 font-semibold">
                    Why This Membership?
                  </p>
                  <div className="space-y-2">
                    {tier.benefits.map((benefit, idx) => (
                      <p key={idx} className="text-xs text-black leading-relaxed">
                        <strong>{benefit.split(' — ')[0]}</strong> — {benefit.split(' — ')[1]}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 mb-8">
                  {tier.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-reset-sage text-lg mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-black leading-relaxed">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                <button className="w-full bg-[#7F9C8B] text-white uppercase text-base font-semibold py-3.5 px-6 min-h-[44px] rounded-lg shadow-[0_2px_8px_rgba(127,156,139,0.25)] hover:bg-[#5E7568] hover:shadow-[0_4px_12px_rgba(127,156,139,0.35)] transition-all duration-200 ease-in-out active:scale-[0.98] whitespace-nowrap cursor-pointer">
                  Join the Waitlist
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAYG & Credits Section */}
      <section className="py-32 px-6 bg-reset-blue-light">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold uppercase mb-6 text-black tracking-wide">
              {membershipContent.payg.title}
            </h2>
            <p className="text-base text-reset-sage leading-relaxed max-w-2xl mx-auto">
              {membershipContent.payg.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {membershipContent.payg.options.map((option, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-8 hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-6">
                  <h3 className="text-2xl font-bold uppercase mb-4 text-black tracking-wide">
                    {option.name}
                  </h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-reset-sage">
                      {option.price}
                    </span>
                  </div>
                  <p className="text-sm text-black leading-relaxed mb-4">
                    {option.description}
                  </p>
                  {option.savings && (
                    <p className="text-xs text-reset-sage font-semibold">
                      {option.savings}
                    </p>
                  )}
                </div>

                {/* Power of 3 Benefits */}
                <div className="mb-6 p-4 bg-reset-sage-light rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-reset-sage mb-3 font-semibold">
                    Why Pay-As-You-Go?
                  </p>
                  <div className="space-y-2">
                    {option.benefits.map((benefit, idx) => (
                      <p key={idx} className="text-xs text-black leading-relaxed">
                        <strong>{benefit.split(' — ')[0]}</strong> — {benefit.split(' — ')[1]}
                      </p>
                    ))}
                  </div>
                </div>

                <button className="w-full bg-[#7F9C8B] text-white uppercase text-base font-semibold py-3.5 px-6 min-h-[44px] rounded-lg shadow-[0_2px_8px_rgba(127,156,139,0.25)] hover:bg-[#5E7568] hover:shadow-[0_4px_12px_rgba(127,156,139,0.35)] transition-all duration-200 ease-in-out active:scale-[0.98] whitespace-nowrap cursor-pointer">
                  Plan Your Visit
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Thermal Suite Options */}
      <section className="py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold uppercase mb-6 text-black tracking-wide">
              {membershipContent.thermal.title}
            </h2>
            <p className="text-base text-reset-sage leading-relaxed max-w-2xl mx-auto">
              {membershipContent.thermal.description}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {membershipContent.thermal.options.map((option, index) => (
              <div
                key={index}
                className="bg-white border border-reset-border rounded-lg p-8 hover:border-reset-sage transition-all duration-300"
              >
                <div className="mb-6">
                  <h3 className="text-2xl font-bold uppercase mb-4 text-black tracking-wide">
                    {option.name}
                  </h3>
                  <div className="mb-4">
                    <span className="text-3xl font-bold text-reset-sage">
                      {option.price}
                    </span>
                  </div>
                  <p className="text-sm text-black leading-relaxed mb-6">
                    {option.description}
                  </p>
                </div>

                {/* Power of 3 Benefits */}
                <div className="mb-6 p-4 bg-reset-sage-light rounded-lg">
                  <p className="text-xs uppercase tracking-wider text-reset-sage mb-3 font-semibold">
                    Why This Option?
                  </p>
                  <div className="space-y-2">
                    {option.benefits.map((benefit, idx) => (
                      <p key={idx} className="text-xs text-black leading-relaxed">
                        <strong>{benefit.split(' — ')[0]}</strong> — {benefit.split(' — ')[1]}
                      </p>
                    ))}
                  </div>
                </div>

                <div className="space-y-3 mb-8">
                  {option.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <i className="ri-check-line text-reset-sage text-lg mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-black leading-relaxed">
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                <button className="w-full bg-[#7F9C8B] text-white uppercase text-base font-semibold py-3.5 px-6 min-h-[44px] rounded-lg shadow-[0_2px_8px_rgba(127,156,139,0.25)] hover:bg-[#5E7568] hover:shadow-[0_4px_12px_rgba(127,156,139,0.35)] transition-all duration-200 ease-in-out active:scale-[0.98] whitespace-nowrap cursor-pointer">
                  {index === 0 ? 'Plan Your Visit' : 'Join the Waitlist'}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Membership Benefits */}
      <section className="py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-bold uppercase mb-6 text-black tracking-wide">
              {membershipContent.benefits.title}
            </h2>
            <p className="text-base text-reset-sage leading-relaxed max-w-2xl mx-auto">
              {membershipContent.benefits.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            {membershipContent.benefits.items.map((benefit, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                  <i className={`${benefit.icon} text-4xl text-reset-sage`}></i>
                </div>
                <h3 className="text-xl font-bold uppercase mb-4 text-black tracking-wide">
                  {benefit.title}
                </h3>
                <p className="text-sm text-black leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-32 px-6 bg-reset-blue-light">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold uppercase mb-6 text-black tracking-wide">
              {membershipContent.included.title}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {membershipContent.included.items.map((item, index) => (
              <div key={index} className="flex items-start gap-4">
                <i className="ri-check-line text-reset-sage text-2xl mt-1 flex-shrink-0"></i>
                <span className="text-base text-black leading-relaxed">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-32 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold uppercase mb-8 text-black tracking-wide">
            {membershipContent.commitment.title}
          </h2>
          <p className="text-base text-black leading-loose mb-12">
            {membershipContent.commitment.description}
          </p>
          <div className="space-y-4">
            {membershipContent.commitment.points.map((point, index) => (
              <p key={index} className="text-sm text-reset-sage leading-relaxed">
                {point}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-[#7F9C8B]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold uppercase mb-6 text-white tracking-wide">
            {membershipContent.cta.title}
          </h2>
          <p className="text-base text-white/90 leading-relaxed mb-10">
            {membershipContent.cta.subtitle}
          </p>
          <button className="bg-white text-[#7F9C8B] uppercase text-base font-semibold py-3.5 px-10 min-h-[44px] rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.1)] hover:bg-[#2C2826] hover:text-white hover:shadow-[0_4px_12px_rgba(0,0,0,0.2)] transition-all duration-200 ease-in-out active:scale-[0.98] whitespace-nowrap cursor-pointer">
            {membershipContent.cta.button}
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
